import { Head, router, usePage } from "@inertiajs/react"
import { Activity, Clock, PackageOpen } from "lucide-react"
import { useState } from "react"

import AgentChangelog from "@/components/agents/agent-changelog"
import { agentExecutionColumns } from "@/components/agents/agent-execution-columns"
import { CancellationBanner } from "@/components/agents/cancellation-banner"
import ImpactSummary from "@/components/agents/impact-summary"
import { OnboardingEmptyState } from "@/components/onboarding-empty-state"
import { PageHeading, PageLayout } from "@/components/page-layout"
import { Card } from "@/components/ui/card"
import { DataTable } from "@/components/ui/data-table"
import { Empty } from "@/components/ui/empty"
import AppLayout from "@/layouts/app-layout"
import { formatRelativeTime } from "@/lib/utils"
import { agentPath, agentsPath } from "@/routes"
import type { Agent, BreadcrumbItem } from "@/types"

type Timeframe = "24_hours" | "7_days" | "30_days" | "3_months" | "12_months"

export default function ShowAgent({
  agent,
  scheduled_cancellation = null,
}: {
  agent: Agent | null
  scheduled_cancellation?: { deactivates_on: string } | null
}) {
  const { account } = usePage<{ account?: { state?: string } }>().props
  const isOnboarding = account?.state === "onboarding"

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Agents",
      href: agentsPath(),
    },
  ]

  // Get initial timeframe from URL or default to 30_days
  const getInitialTimeframe = (): Timeframe => {
    if (typeof window === "undefined") return "30_days"
    const params = new URLSearchParams(window.location.search)
    const timeRange = params.get("time_range") as Timeframe | null
    const validTimeframes: Timeframe[] = [
      "24_hours",
      "7_days",
      "30_days",
      "3_months",
      "12_months",
    ]
    return timeRange && validTimeframes.includes(timeRange)
      ? timeRange
      : "30_days"
  }

  const [timeframe, setTimeframe] = useState<Timeframe>(getInitialTimeframe)

  const handleTimeframeChange = (value: string) => {
    if (!value) return
    const timeframeValue = value as Timeframe
    setTimeframe(timeframeValue)
    if (!agent) return

    const url = new URL(window.location.href)
    url.searchParams.set("time_range", timeframeValue)

    router.visit(url.pathname + url.search, {
      preserveState: false,
      preserveScroll: false,
    })
  }

  if (!agent) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title="Agent Not Found" />

        <div className="flex h-full flex-1 flex-col gap-4 overflow-x-auto rounded-xl p-4 sm:p-6 lg:p-10">
          <div className="flex h-full flex-col items-center justify-center text-center">
            <h1 className="mb-2 text-2xl font-semibold">404</h1>
            <p className="text-muted-foreground">
              The agent you&apos;re looking for doesn&apos;t exist or isn&apos;t
              visible.
            </p>
          </div>
        </div>
      </AppLayout>
    )
  }

  breadcrumbs.push({
    title: agent.name,
    href: agentPath({ id: agent.uid }),
  })

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      {isOnboarding ? (
        <OnboardingEmptyState
          icon={PackageOpen}
          title="This agent is being configured"
          description="Imperative is working on configuring this agent. Once they're ready, you'll be able to monitor them here."
        />
      ) : (
        <PageLayout>
          {scheduled_cancellation && (
            <CancellationBanner
              agentUid={agent.uid}
              deactivatesOn={scheduled_cancellation.deactivates_on}
            />
          )}

          <PageHeading>{agent.name}</PageHeading>

          <ImpactSummary
            agent={agent}
            timeframe={timeframe}
            onTimeframeChange={handleTimeframeChange}
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            {/* Updates from Imperative - Left Column */}
            <div className="lg:col-span-2">
              <h2 className="mb-4 font-semibold">Updates from Imperative</h2>
              <AgentChangelog entries={agent.agent_changelog_entries ?? []} />
            </div>

            {/* Recent Activity - Right Column */}
            <div className="lg:col-span-3">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-semibold">Recent Activity</h2>
                <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>
                    {agent.last_execution_at
                      ? `Last active: ${formatRelativeTime(agent.last_execution_at)}`
                      : "No recent activity"}
                  </span>
                </div>
              </div>
              <DataTable
                columns={agentExecutionColumns}
                data={agent.agent_executions ?? []}
                pagy={agent.pagy}
                onPageChange={(page) => {
                  const url = new URL(window.location.href)
                  url.searchParams.set("page", page.toString())
                  if (timeframe !== "30_days") {
                    url.searchParams.set("time_range", timeframe)
                  }
                  router.visit(url.pathname + url.search, {
                    preserveState: true,
                    preserveScroll: true,
                  })
                }}
                embedded={true}
                emptyComponent={
                  <Card className="px-6 shadow-none">
                    <Empty
                      icon={<Activity className="size-4" />}
                      title="No activity yet"
                      description="Agent runs will appear here once this agent has been executed for the first time."
                    />
                  </Card>
                }
              />
            </div>
          </div>
        </PageLayout>
      )}
    </AppLayout>
  )
}
